import { getOrderLabelQuery } from '@/api/orders/orderLabelQueries';
import { getOrderQuery } from '@/api/orders/orderQueries';
import LifetelosLogo from '@/assets/logos/lifetelos.svg?react';
import JokubasPharmStampIcon from '@/assets/logos/signatures/jokubas-pharm.svg?react';
import { Button } from '@/components/common/Button';
import type { OrderItem } from '@/domain/Order';
import {
  LEGAL_DISCLAIMER,
  buildCharacterisingSubstancesLine,
  collectLabelAllergens,
  earliestBestBefore,
  formatLabelMonthYear,
  formatNetQuantityDisplay,
  formatNrvDisplay,
  formatOtherIngredients,
  highlightAllergenWords,
} from '@/lib/utils/supplementLabel';
import { useQuery } from '@tanstack/react-query';
import { toPng } from 'html-to-image';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';

interface SupplementData {
  item: OrderItem;
  quantity: number;
  type: string;
}

interface SupplementFactsPreviewProps {
  orderId: number;
  name: string;
  supplements: Array<SupplementData>;
  instruction: string;
  onBack: () => void;
}

/** Physical label sticker size. */
const LABEL_WIDTH_MM = 100;
const LABEL_HEIGHT_MM = 150;

/** Design canvas width the layout is authored against. */
const DESIGN_WIDTH_PX = 846;

function mmToPx(mm: number): number {
  return (mm / 25.4) * 96;
}

/** Normalize "500mg" → "500 mg" to match print reference. */
function formatAmountDisplay(amount: string): string {
  return amount
    .replace(/(\d+(?:[.,]\d+)?)\s*(mg|g|µg|ug|mcg|IU|iu)\b/gi, (_, n, u) => {
      const unit =
        u.toLowerCase() === 'ug' || u.toLowerCase() === 'mcg' ? 'µg' : u;
      return `${n} ${unit}`;
    })
    .replace(/\s+/g, ' ')
    .trim();
}

function fitScale(widthPx: number, heightPx: number): number {
  const targetW = mmToPx(LABEL_WIDTH_MM);
  const targetH = mmToPx(LABEL_HEIGHT_MM);
  if (widthPx <= 0 || heightPx <= 0) return 1;
  return Math.min(targetW / widthPx, targetH / heightPx);
}

export function SupplementFactsPreview({
  orderId,
  name,
  supplements,
  instruction,
  onBack,
}: SupplementFactsPreviewProps) {
  const selectedItems = supplements.map((s) => s.item);
  // Order line items are keyed by price_id (ItemPrice id), not a row `id`.
  const itemIds = [
    ...new Set(
      selectedItems
        .map((item) => item.price_id ?? item.id)
        .map((id) => (id == null ? NaN : Number(id)))
        .filter((id) => Number.isFinite(id) && id > 0),
    ),
  ].sort((a, b) => a - b);

  const { data: orderResponse } = useQuery(getOrderQuery(orderId));
  const {
    data: labelResponse,
    isLoading: isLabelLoading,
    isError: isLabelError,
    error: labelError,
  } = useQuery(getOrderLabelQuery(orderId, itemIds));

  const order = orderResponse?.data;
  const label = labelResponse?.data;
  const orderLotNumber = order?.lot_number;

  const customerName =
    name.trim() ||
    [order?.user?.name, order?.user?.surname].filter(Boolean).join(' ') ||
    '';

  const createdRaw =
    label?.created_at ||
    order?.updated_at ||
    order?.time_paid ||
    order?.created_at ||
    null;
  const createdDate = formatLabelMonthYear(createdRaw);
  const bestBefore = earliestBestBefore(label, [
    ...selectedItems,
    ...(order?.items ?? []),
  ]);

  const labelIngredients = label?.ingredients ?? [];
  const otherIngredients = formatOtherIngredients(
    selectedItems.length > 0 ? selectedItems : (order?.items ?? []),
  );
  const otherIngredientSegments = highlightAllergenWords(
    otherIngredients,
    collectLabelAllergens(labelIngredients, label?.allergens),
  );

  const categoryLine = buildCharacterisingSubstancesLine(
    labelIngredients,
    label?.categories,
  );

  const labelContentRef = useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = useState(1);

  useLayoutEffect(() => {
    const el = labelContentRef.current;
    if (!el) return;

    const updateScale = () => {
      setPreviewScale(fitScale(el.offsetWidth, el.offsetHeight));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    label,
    customerName,
    instruction,
    otherIngredients,
    createdDate,
    bestBefore,
    categoryLine,
  ]);

  /**
   * Safari + thermal printers mishandle CSS transform when printing HTML,
   * leaving the label tiny in the corner. Rasterize the on-screen 100×150 mm
   * preview to a PNG and print that image full-bleed instead.
   */
  const handlePrint = async () => {
    const viewport = document.getElementById('lifetelos-label-viewport');
    if (!viewport) return;

    let dataUrl: string;
    try {
      dataUrl = await toPng(viewport, {
        cacheBust: true,
        pixelRatio: 3,
        backgroundColor: '#ffffff',
        // Avoid copying dialog/ancestor transforms into the capture.
        style: {
          transform: 'none',
          boxShadow: 'none',
        },
      });
    } catch (error) {
      console.error('Failed to render label for print', error);
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Print label');
    Object.assign(iframe.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: `${LABEL_WIDTH_MM}mm`,
      height: `${LABEL_HEIGHT_MM}mm`,
      border: '0',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '-1',
    });
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) {
      iframe.remove();
      return;
    }

    doc.open();
    doc.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      @page {
        size: ${LABEL_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm;
        margin: 0;
      }
      html, body {
        margin: 0;
        padding: 0;
        width: ${LABEL_WIDTH_MM}mm;
        height: ${LABEL_HEIGHT_MM}mm;
        overflow: hidden;
        background: white;
      }
      img {
        display: block;
        width: ${LABEL_WIDTH_MM}mm;
        height: ${LABEL_HEIGHT_MM}mm;
        object-fit: fill;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    </style>
  </head>
  <body>
    <img id="label" src="${dataUrl}" alt="Label" />
  </body>
</html>`);
    doc.close();

    try {
      const img = doc.getElementById('label') as HTMLImageElement | null;
      if (img && !img.complete) {
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        });
      }
      await new Promise((r) => setTimeout(r, 50));
      win.focus();
      win.print();
    } finally {
      setTimeout(() => iframe.remove(), 1000);
    }
  };

  return (
    <div className="font-inter flex max-h-[90vh] flex-col bg-neutral-300">
      <div className="flex flex-1 flex-col items-center overflow-auto p-4">
        {(isLabelLoading || isLabelError) && (
          <div className="mb-3 w-full max-w-[100mm]">
            {isLabelLoading && (
              <p className="text-sm text-neutral-700">Loading label data…</p>
            )}
            {isLabelError && (
              <p className="text-sm text-red-700">
                Failed to load label:{' '}
                {labelError instanceof Error
                  ? labelError.message
                  : 'Unknown error'}
              </p>
            )}
          </div>
        )}

        <p className="mb-2 text-xs text-neutral-600">
          Label size: {LABEL_WIDTH_MM} × {LABEL_HEIGHT_MM} mm
        </p>

        {/* Fixed physical viewport — content always scaled to fit */}
        <div
          id="lifetelos-label-viewport"
          className="relative shrink-0 overflow-hidden bg-white shadow-lg"
          style={{
            width: `${LABEL_WIDTH_MM}mm`,
            height: `${LABEL_HEIGHT_MM}mm`,
          }}
        >
          <div
            id="lifetelos-label-page"
            ref={labelContentRef}
            className="font-inter box-border bg-white tracking-[0.02em]! text-black"
            style={{
              width: DESIGN_WIDTH_PX,
              transform: `scale(${previewScale})`,
              transformOrigin: 'top left',
            }}
          >
            <div className="box-border flex flex-col px-6 pt-6 pb-4">
              {/* Top block */}
              <div className="mb-5 flex justify-between">
                <div className="flex w-[418px] min-w-0 flex-col">
                  <LifetelosLogo className="mb-4 h-11.5 w-[243px]" />

                  <p className="leading mb-6 text-base font-normal">
                    {categoryLine}
                  </p>

                  {customerName && (
                    <h2 className="mb-3 text-[34px] leading-[1.2] font-normal">
                      {customerName}
                    </h2>
                  )}

                  {instruction && (
                    <p className="text-base leading-[1.4] font-normal">
                      {instruction}
                    </p>
                  )}
                </div>

                <div className="flex w-[240px] shrink-0 flex-col items-end">
                  <div className="mb-8 text-right text-base leading-[1.4]">
                    {label && (
                      <p className="text-right font-semibold">
                        {formatNetQuantityDisplay(
                          label.packets,
                          label.net_quantity_grams,
                        )}
                      </p>
                    )}
                    {orderLotNumber && <p>#{orderLotNumber}</p>}
                  </div>

                  <div className="-mb-2 flex flex-1 translate-x-1.25 items-center">
                    <JokubasPharmStampIcon className="h-[110px] w-[240px]" />
                  </div>

                  <div className="text-base leading-[1.4]">
                    <p>
                      <span>Created:</span> {createdDate ?? '—'}
                    </p>
                    <p>
                      <span>Best before:</span> {bestBefore ?? '-'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4 flex justify-between pr-8 text-base leading-[1.4] font-medium">
                <span>Amount per serving</span>
                <span>%NRV*</span>
              </div>

              <table className="mb-5 w-full border-collapse border border-black text-base text-black">
                <colgroup>
                  <col className="w-1/3" />
                  <col className="w-1/3" />
                  <col className="w-1/3" />
                </colgroup>
                <tbody className="text-base leading-[1.4]">
                  {labelIngredients.map((ingredient, index) => (
                    <tr key={`${ingredient.name}-${index}`}>
                      <td className="border border-black bg-[#F7F7F7] py-2.5 pr-4 pl-7 font-normal">
                        {ingredient.name}
                      </td>
                      <td className="border border-black bg-[#F7F7F7] px-4 py-2.5 text-center">
                        {formatAmountDisplay(ingredient.amount)}
                      </td>
                      <td className="border border-black bg-[#F7F7F7] py-2.5 pr-4 pl-17.5">
                        {formatNrvDisplay(ingredient.nrv_percent)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Other ingredients + NRV legend */}
              <div className="mb-6 flex items-start justify-between text-base leading-[1.4]">
                <div className="max-w-[455px] min-w-0 flex-1 space-y-3 text-black">
                  {otherIngredients && (
                    <p>
                      <span className="leading-[25.6px] font-medium">
                        Other ingredients <br />
                      </span>{' '}
                      {otherIngredientSegments.map((segment, index) =>
                        segment.bold ? (
                          <strong key={index} className="font-bold">
                            {segment.text}
                          </strong>
                        ) : (
                          <Fragment key={index}>{segment.text}</Fragment>
                        ),
                      )}
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-base leading-[1.6] text-black">
                  <p>*Nutritional reference value</p>
                  <p>†NRV not established.</p>
                </div>
              </div>

              {/* Manufacturer */}
              <div className="mb-6 text-base leading-[1.8] text-black">
                <p className="flex items-center gap-2">
                  <span>Manufactured for:</span>
                  <LifetelosLogo className="h-5 w-[110px]" />
                </p>
                <p>
                  Sveiko gyvenimo papildai, MB | Santariškių g. 5 Vilnius
                  Lithuania 08406
                </p>
                <p>info@lifetelos.com</p>
                <p>www.lifetelos.com</p>
              </div>

              {/* Legal disclaimer */}
              <p className="text-[14px] leading-[1.4] text-black">
                {LEGAL_DISCLAIMER}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 border-t border-neutral-400 bg-white px-4 py-3">
        <Button
          className="bg-neutral-700 px-4 text-base text-white hover:bg-neutral-600"
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          className="bg-black px-4 text-base text-white hover:bg-neutral-800"
          onClick={() => void handlePrint()}
          disabled={!label}
        >
          Print 100×150 mm
        </Button>
      </div>
    </div>
  );
}
